import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Combobox, Transition } from '@headlessui/react';
import { IoIosCloseCircle as ClearIcon } from 'react-icons/io';

import { HiOutlineChevronDoubleDown } from 'react-icons/hi';

function FormSelectStatic({
  data,
  name,
  value,
  readOnly,
  onChange,
  placeholder,
  inputClassName,
  containerClassName,
  optionsItemClassName,
  optionsContainerClassName,
  withClear,
  callBack,
  onQueryChange,
  ...otherProps
}) {
  const [selected, setSelected] = useState(value);
  const [query, setQuery] = useState('');

  const filteredData =
    onQueryChange || query === ''
      ? data
      : data?.filter((item) =>
          item?.label?.toLowerCase()?.includes(query?.toLowerCase()),
        );

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    if (typeof onQueryChange === 'function') {
      onQueryChange(e.target.value);
    }
  };

  const handleClickClear = () => setSelected({ label: '', value: '' });

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: selected,
      }));
    }
    if (typeof callBack === 'function') {
      callBack(selected?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, name, onChange]);

  return (
    <Combobox value={selected} onChange={setSelected}>
      <div className={cn('w-[269px] relative', containerClassName)}>
        <div className="relative w-full text-left bg-white rounded-sm cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-teal-300 focus-visible:ring-offset-2 sm:text-sm overflow-hidden">
          <Combobox.Input
            className={cn(
              'w-full bg-light-gray-50 border-none text-gray-500 focus:ring-0 py-[0.6rem] pl-3 pr-10 font-[500] leading-5 text-xs',
              inputClassName,
            )}
            displayValue={(item) => (item?.value ? item?.label : '')}
            placeholder={placeholder}
            onChange={handleQueryChange}
            readOnly={readOnly}
            {...otherProps}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1 ">
            {selected?.label && withClear && (
              <ClearIcon
                onClick={handleClickClear}
                aria-hidden="true"
                className="text-red-500 text-sm"
              />
            )}

            <HiOutlineChevronDoubleDown
              className="w-3 h-3 text-gray-500"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery('')}
        >
          <Combobox.Options
            className={cn(
              'absolute z-20 w-full top-10 left-0 shadow-lg max-h-[500px] overflow-y-auto',
              optionsContainerClassName,
            )}
          >
            {filteredData?.length === 0 && query !== '' ? (
              <div className="cursor-default bg-white select-none relative py-2 px-4 text-gray-700 text-center">
                Nothing found.
              </div>
            ) : (
              filteredData?.map((item) => (
                <Combobox.Option
                  key={item?.value}
                  value={item}
                  className={() =>
                    `text-xs select-none relative py-2 pl-3 pr-4 font-[500] ${optionsItemClassName} ${
                      selected?.value === item?.value
                        ? 'bg-gray-700 cursor-default text-white'
                        : 'text-gray-500 cursor-pointer hover:bg-gray-100 bg-white'
                    }`
                  }
                >
                  {item?.label}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}

FormSelectStatic.defaultProps = {
  readOnly: true,
  placeholder: 'Select',
  inputClassName: '',
  containerClassName: '',
  optionsItemClassName: '',
  optionsContainerClassName: '',
  withClear: true,
  onChange: false,
  callBack: false,
  onQueryChange: false,
};

FormSelectStatic.propTypes = {
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  inputClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  optionsItemClassName: PropTypes.string,
  optionsContainerClassName: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  callBack: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onQueryChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  data: PropTypes.instanceOf(Array).isRequired,
  value: PropTypes.instanceOf(Object).isRequired,
  withClear: PropTypes.bool,
};

export default FormSelectStatic;
